import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { ChangeEvent, useEffect, useState } from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  getIssueDetailsRequest,
  getIssueDetailsSuccess,
  getOrderIssuesCountRequest,
  getOrderIssuesRequest,
} from "../../../../redux/orders/slice";
import Loading from "../../../../components/loading/loading";
import EditIssue from "./edit-issue";
import { updateIssueRequest } from "../../../../utils/APICalls";
import dayjs from "dayjs";
import ErrorNotification from "../../../../components/error-notification/error-notification";
import { userType } from "../../../../utils/Constant";

export default function IssueDetails({
  showDialog,
  closeDialog,
  issueId,
  orderId,
}: any) {
  const dispatch = useDispatch();
  const [showEdit, setShowEdit] = useState(false);
  const [updateContainer, setUpdateContainer] = useState(false);
  const [updateTextFieldValue, setUpdateTextFieldValue] = useState("");
  const [updateError, setUpdateError] = useState(false);
  const [checked, setChecked] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { loading, issueDetails } = useSelector((state: any) => state.orders);
  const currentUser = useSelector((state: any) => state.user.user?.currentUser);

  useEffect(() => {
    if (issueId) {
      dispatch(getIssueDetailsSuccess({ response: null }));
      dispatch(getIssueDetailsRequest({ issueId }));
    }
  }, [issueId]); // eslint-disable-line

  const updateButtonClick = () => {
    setUpdateContainer(true);
  };

  const handleUpdateValueChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUpdateTextFieldValue(e.target.value);
  };

  const handleUpdateSaveClick = () => {
    if (!updateTextFieldValue.trim()) {
      setUpdateError(true);
      return;
    }
    setUpdateError(false);
    setUpdateContainer(false);

    const payload = {
      description: updateTextFieldValue,
      isResolved: checked,
      issueIds: [issueId],
    };
    updateIssue(payload);
  };

  const updateIssue = async (payload: any) => {
    setShowLoading(true);
    const response = await updateIssueRequest(payload);
    setShowLoading(false);
    if (response?.status === 204) {
      dispatch(getIssueDetailsRequest({ issueId }));
      if (orderId) {
        dispatch(
          getOrderIssuesRequest({
            page: 1,
            pageSize: 10,
            orderId,
            sortColumn: "is_resolved",
            sortOrder: "asc",
          })
        );
      } else {
        dispatch(
          getOrderIssuesRequest({
            page: 1,
            pageSize: 10,
            sortColumn: "is_resolved",
            sortOrder: "asc",
          })
        );
      }
      dispatch(getOrderIssuesCountRequest({ orderId }));
    } else if (response?.status === 400) {
      setShowErrorNotification(true);
      setErrorMessage(response?.data?.detail);
    }
  };

  return (
    <>
      <Box>
        <Dialog
          open={showDialog}
          onClose={closeDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          fullWidth={true}
          maxWidth={"lg"}
        >
          <DialogTitle id="responsive-dialog-title">
            <Typography variant="h5" color="black">
              Issue Details
            </Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={closeDialog}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,

              color: "black",
            })}
          >
            <CloseIcon />
          </IconButton>

          <DialogContent dividers>
            <DialogContentText id="alert-dialog-description">
              {showEdit ? (
                <EditIssue
                  orders={issueDetails?.orders}
                  issueId={issueId}
                  orderId={orderId}
                  issueDetails={issueDetails}
                  onHide={() => setShowEdit(false)}
                />
              ) : (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="h5" color="black">
                      Issue
                    </Typography>
                    {!issueDetails?.isResolved &&
                      currentUser?.userType === userType.tenant && (
                        <Button
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => setShowEdit(true)}
                        >
                          Edit Issue
                        </Button>
                      )}
                  </Box>
                  <Box className="display-name-layout">
                    <Typography className="label-txt">Issue Type</Typography>
                    <Typography color="black">
                      {issueDetails?.type
                        ? issueDetails?.type === "Tenant"
                          ? "Internal Issue"
                          : `${issueDetails?.type} Issue`
                        : "-"}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box className="user-name-layout">
                    <Typography className="label-txt">
                      Issue Sub Type
                    </Typography>
                    <Typography
                      sx={{
                        textTransform:
                          issueDetails?.subType === "Qc"
                            ? "uppercase"
                            : "capitalize",
                      }}
                      color="black"
                    >
                      {issueDetails?.subType === "ContractorIssue"
                        ? "Contractor"
                        : issueDetails?.subType || "-"}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box className="user-name-layout">
                    <Typography className="label-txt">
                      Selected Orders
                    </Typography>
                    <Box>
                      {issueDetails?.orders?.map(
                        (order: any, index: number) => (
                          <Chip
                            sx={{ ml: index === 0 ? 0 : 1 }}
                            label={order?.orderNumber}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )
                      )}
                    </Box>
                  </Box>
                  <Divider />
                  <Box className="user-name-layout">
                    <Box
                      sx={{
                        minWidth: 192,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography fontSize={12} color="rgba(0, 0, 0, 0.6)">
                        Assigned Users
                      </Typography>
                      <Tooltip
                        title="Assigned users will see this issue in their dashboard and issues queue. Assigned users will be able to resolve the issue and clear the flag."
                        arrow
                        placement="top"
                      >
                        <IconButton sx={{ p: 0, mr: 2 }}>
                          <InfoOutlinedIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box>
                      {issueDetails?.tenantUsers &&
                        issueDetails?.tenantUsers.length > 0 && (
                          <Box display={"flex"} alignItems={"center"} gap={2}>
                            <Typography color="black" minWidth={120}>
                              Internal Users
                            </Typography>
                            <Box>
                              {issueDetails?.tenantUsers?.map((user: any) => (
                                <Chip
                                  sx={{ ml: 1 }}
                                  label={user?.fullName}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                      {issueDetails?.clientUsers &&
                        issueDetails?.clientUsers.length > 0 && (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={2}
                            mt={1}
                          >
                            <Typography color="black" minWidth={120}>
                              Client
                            </Typography>
                            <Box>
                              {issueDetails?.clientUsers?.map((user: any) => (
                                <Chip
                                  sx={{ ml: 1 }}
                                  label={user?.fullName}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Box>
                        )}

                      {issueDetails?.inspectorUsers &&
                        issueDetails?.inspectorUsers.length > 0 && (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            gap={2}
                            mt={1}
                          >
                            <Typography color="black" minWidth={120}>
                              Inspector
                            </Typography>
                            <Box>
                              {issueDetails?.inspectorUsers?.map(
                                (user: any) => (
                                  <Chip
                                    sx={{ ml: 1 }}
                                    label={user?.fullName}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )
                              )}
                            </Box>
                          </Box>
                        )}
                    </Box>
                  </Box>
                  <Divider />
                  <Box className="user-name-layout">
                    <Typography className="label-txt">Issue</Typography>
                    <Typography color="black">
                      {issueDetails?.description || "-"}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box className="user-name-layout">
                    <Typography className="label-txt">Priority</Typography>
                    <Typography color="error">
                      {issueDetails?.priority || "-"}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box className="user-name-layout">
                    <Typography className="label-txt">
                      Put Job on Hold Until <br /> Resolved?
                    </Typography>
                    <Typography color="error">
                      {issueDetails?.onHoldUntilResolved ? "Yes" : "No"}
                    </Typography>
                  </Box>
                </>
              )}
              <Box display={"flex"} justifyContent={"space-between"} mt={2}>
                <Typography variant="h5" color="black">
                  Updates
                </Typography>
                {!issueDetails?.isResolved && !updateContainer && (
                  <Button
                    variant="outlined"
                    startIcon={<AddOutlinedIcon />}
                    onClick={updateButtonClick}
                  >
                    Add Update
                  </Button>
                )}
              </Box>
              <Typography
                sx={{
                  borderRadius: 1,
                  p: 1.5,
                  border: "1.5px solid #E0E0E0",
                  mt: 2,
                  fontStyle: "italic",
                }}
              >
                Issue Created{" "}
                {dayjs(issueDetails?.createdOrUpdatedAt).format("MMM D, YYYY")}{" "}
                by {issueDetails?.createdByUser?.fullName}
              </Typography>
              {issueDetails?.orderIssueUpdates &&
                issueDetails?.orderIssueUpdates?.map(
                  (update: any, index: number) => (
                    <Box
                      sx={{
                        border: "1px solid #E0E0E0",
                        mt: 2,
                        padding: 1.5,
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography fontSize={15} fontStyle={"italic"}>
                          {dayjs(update?.createdAt).format("MMM D, YYYY")} by{" "}
                          {update?.createdByUser?.fullName}
                        </Typography>
                        {issueDetails?.isResolved ? (
                          <Box display={"flex"}>
                            {index ===
                              issueDetails?.orderIssueUpdates.length - 1 && (
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  backgroundColor: "green",
                                  p: 1,
                                  color: "white",
                                  borderRadius: 10,
                                  height: 6,
                                  display: "flex",
                                  alignItems: "center",
                                  mr: 2,
                                }}
                              >
                                Resolved
                              </Typography>
                            )}
                            <LockOutlinedIcon sx={{ fontSize: 20 }} />
                          </Box>
                        ) : (
                          <LockOutlinedIcon sx={{ fontSize: 20 }} />
                        )}
                      </Box>
                      <Typography mt={2} color="black">
                        {update?.description || "-"}
                      </Typography>
                    </Box>
                  )
                )}
              <Box>
                {updateContainer && (
                  <Box
                    sx={{
                      border: "1px solid #E0E0E0",
                      px: 1,
                      mt: 2,
                      borderRadius: 1,
                    }}
                  >
                    <Box className="user-name-layout">
                      <Typography className="label-txt">Update</Typography>
                      <TextField
                        fullWidth
                        minRows={4}
                        value={updateTextFieldValue}
                        multiline
                        onChange={handleUpdateValueChange}
                        helperText={
                          updateError ? "This field cannot be empty" : ""
                        }
                        slotProps={{
                          formHelperText: {
                            sx: { color: "red" },
                          },
                        }}
                      />
                    </Box>

                    <Box className="user-name-layout">
                      <Typography className="label-txt">
                        Issue Resolved?
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={checked}
                              value={checked}
                              onChange={(e) => setChecked(e.target.checked)}
                            />
                          }
                          label={checked ? "Yes" : "No"}
                        />
                        <Box>
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setUpdateContainer(false);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            sx={{ ml: 2 }}
                            onClick={handleUpdateSaveClick}
                          >
                            save
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            </DialogContentText>
            {(loading || showLoading) && <Loading />}
            {showErrorNotification && (
              <ErrorNotification
                show={showErrorNotification}
                onClose={() => setShowErrorNotification(false)}
                title={errorMessage}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={closeDialog}>
              Done
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
}
